import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum } from "../../lib/utils";
import { ProvenanceTrigger } from "../provenance/ProvenanceTrigger";
import { Button } from "../ui/button";
import {
  Banknote, Landmark, PiggyBank, Leaf, ShieldCheck, ArrowRight, AlertTriangle, Info, CircleAlert,
} from "lucide-react";

const CARD_DEFS = [
  { key: "sustainability_capex", label: "Sustainability CAPEX", icon: Banknote, fmt: (v) => formatSgd(v), pkey: "susfin.capex" },
  { key: "potential_funding", label: "Potential Funding", icon: Landmark, fmt: (v) => formatSgd(v), pkey: "susfin.funding" },
  { key: "annual_savings", label: "Annual Savings", icon: PiggyBank, fmt: (v) => formatSgd(v), pkey: "susfin.savings" },
  { key: "carbon_reduction_t", label: "Carbon Reduction", icon: Leaf, fmt: (v) => `${formatNum(v, { decimals: 1 })} t`, pkey: "susfin.carbon" },
  { key: "climate_risk_addressed", label: "Climate Risk Addressed", icon: ShieldCheck, fmt: (v) => formatSgd(v), pkey: "susfin.risk" },
];
const SEV = {
  high: { icon: CircleAlert, cls: "text-red-400" },
  medium: { icon: AlertTriangle, cls: "text-[#FFB800]" },
  low: { icon: Info, cls: "text-muted-foreground" },
};

export default function SusfinOverview({ onNavigate }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/susfin/overview").then((r) => setD(r.data)).catch(() => setD({ empty: true })); }, []);
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading overview…</div>;
  if (d.empty) return <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-overview-empty">No data yet.</div>;

  const pipeline = d.pipeline || [];
  const totalInvestments = pipeline.reduce((s, x) => s + (x.count || 0), 0);
  const topStage = pipeline.reduce((a, b) => ((b.count || 0) > (a?.count || 0) ? b : a), pipeline[0] || null);
  return (
    <div className="space-y-6" data-testid="sf-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {CARD_DEFS.map(({ key, label, icon: Icon, fmt, pkey }) => (
          <div key={key} className="card-flat p-4 group" data-testid={`sf-card-${key}`}>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Icon className="h-4 w-4 text-accent" /><span className="label-eyebrow text-[10px]">{label}</span>
              {pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}
            </div>
            <div className="font-mono text-2xl font-bold tracking-tight">{fmt(d.cards[key] || 0)}</div>
          </div>
        ))}
      </div>

      <div className="card-flat p-4 flex items-center justify-between gap-3" data-testid="sf-pipeline-summary">
        <div className="text-sm text-muted-foreground">
          {totalInvestments > 0 && topStage ? (
            <>
              <span className="font-semibold text-foreground">{topStage.count} of {totalInvestments}</span>{" "}
              investment{totalInvestments === 1 ? "" : "s"} in{" "}
              <span className="font-semibold text-foreground">{topStage.label}</span>{" "}
              · full pipeline breakdown lives in CFO Portfolio
            </>
          ) : (
            <>No investments in the register yet — add one to build your pipeline.</>
          )}
        </div>
        <button onClick={() => onNavigate?.("dashboards")} className="text-xs text-accent hover:underline inline-flex items-center gap-1 shrink-0" data-testid="sf-pipeline-open-cfo">
          View CFO Portfolio <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <div className="card-flat p-5" data-testid="sf-actions">
        <div className="label-eyebrow mb-3">Priority Actions</div>
        {d.actions.length === 0 && <p className="text-sm text-muted-foreground">Nothing outstanding — the module is up to date.</p>}
        <div className="space-y-2">
          {d.actions.map((a) => {
            const S = SEV[a.severity] || SEV.low;
            return (
              <div key={a.key} className="flex items-center gap-3 border border-border rounded-sm p-3" data-testid={`sf-action-${a.key}`}>
                <S.icon className={`h-4 w-4 shrink-0 ${S.cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.detail}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => onNavigate?.(a.tab)} data-testid={`sf-action-go-${a.key}`}>
                  Go <ArrowRight className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
